'use client';
import { AgentData } from '@/lib/services/agent.service';
import {
  QuickBuyInfo,
  useTransactionConfig,
} from '@/lib/services/user.service';
import {
  Box,
  Text,
  HStack,
  VStack,
  Spinner,
  SimpleGrid,
} from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import AgentItem from './AgentItem';
import {
  useInfiniteAgentList,
  useAgentNewerCount,
  fetchAgentList,
} from '@/lib/services/agent.service';
import { useQueryClient } from '@tanstack/react-query';
import { uniqBy } from '@/lib/utils/processData.utils';
import { useLoginRedirect } from '@/lib/hooks/useLoginRedirect';
import { useMemo, useRef, useEffect, useState } from 'react';
import { AgentItemSkeleton } from '@/components/ui/skeletons/ai-agent/AgentItemSkeleton';
import { useChainInfo } from '@/lib/hooks/useChainInfo';

function scrollToTop(scrollRef: React.RefObject<HTMLDivElement>) {
  if (scrollRef.current) {
    scrollRef.current.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
}

interface AgentListProps {
  version: string;
}

export function AgentList({ version }: AgentListProps) {
  const t = useTranslations('HomePage');
  const { isLogin, openLoginModal } = useLoginRedirect();
  const { chainId } = useChainInfo();
  const [newData, setNewData] = useState<AgentData[]>([]);
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteAgentList(10, version, chainId);
  const listData = data?.pages.flatMap((page) => page?.messages ?? []) ?? [];

  const list = uniqBy([...newData, ...listData], 'id');

  const { data: countData } = useAgentNewerCount(
    version,
    list[0]?.id ?? '',
    '0',
    chainId,
  );

  const { data: transactionConfig } = useTransactionConfig(Number(chainId));

  const quickBuyInfo = transactionConfig.quickBuyList[0].quickBuyInfo;

  const quickBuyInfoList = useMemo(() => {
    if (quickBuyInfo) {
      let list: any = [];
      let keys = Object.keys(quickBuyInfo);
      keys.forEach((item: string) => {
        list.push({
          name: item,
          value: quickBuyInfo[item as keyof QuickBuyInfo],
        });
      });
      return list;
    }
    return [];
  }, [quickBuyInfo]);

  useEffect(() => {
    if (countData && countData?.count > 0 && isLogin) {
      fetchAgentList(
        1,
        countData?.count + 1,
        version,
        chainId,
        '0',
        listData[0]?.id,
      ).then((res) => {
        let data: AgentData[] = [...res.messages];
        setNewData(data);
      });
    }
  }, [countData, isLogin]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  return (
    <Box pos="relative" w="full">
      {countData && countData.count > 0 && list.length > 0 && (
        <HStack
          justifyContent={'center'}
          alignItems={'center'}
          cursor={'pointer'}
          bgColor={'rgba(171, 234, 69, 0.1)'}
          zIndex={1000}
          h={'32px'}
          pos="absolute"
          left={0}
          right={0}
          top={'4px'}
          onClick={() => {
            if (!isLogin) {
              openLoginModal();
              return;
            } else {
              scrollToTop(scrollRef);
            }
          }}
        >
          <Text color={'text.primary'} fontSize={'12px'}>
            {t('agent.clickToViewLatest')}
          </Text>
        </HStack>
      )}
      {isLoading ? (
        <Box h="calc(100vh - 200px)" overflowY="scroll" w="full">
          {Array.from({ length: 10 }).map((_, index) => (
            <AgentItemSkeleton key={index} />
          ))}
        </Box>
      ) : (
        <VStack
          p={'16px'}
          h="calc(100vh - 200px)"
          overflowY="scroll"
          ref={scrollRef}
          onScroll={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            if (
              scrollHeight - scrollTop - clientHeight <= 0 &&
              hasNextPage &&
              !isFetchingNextPage
            ) {
              if (isLogin) {
                fetchNextPage?.();
              } else {
                openLoginModal();
              }
            }
          }}
          scrollbar="hidden"
        >
          {list.map((item: AgentData) => (
            <Box w={'100%'} mb={'16px'} key={item?.id}>
              <AgentItem message={item} quickBuyInfo={quickBuyInfoList} />
            </Box>
          ))}
        </VStack>
      )}
      {isFetchingNextPage ? (
        <VStack
          w="full"
          pos="absolute"
          bg="rgba(0, 0, 0, 0.5)"
          bottom={1}
          left={0}
          right={0}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Spinner color="text.t2" />
          <Text color="text.t2">{t('loading')}...</Text>
        </VStack>
      ) : null}
    </Box>
  );
}
